import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Plus, QrCode, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errText } from "@/lib/api";

const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"],
  ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const OPTIONAL_SLOTS = [
  { key: "morning_tea", name: "Morning tea", time: "7:00 - 8:00 AM" },
  { key: "evening_tea", name: "Evening tea", time: "4:30 - 5:30 PM" },
  { key: "late_snack", name: "Late night snack", time: "10:30 - 11:30 PM" },
];

const TAG_OPTIONS = [
  { value: "", label: "Auto", title: "Detect veg / non-veg from the dish names", on: "border-foreground bg-foreground" },
  { value: "veg", label: "Veg", title: "Mark every dish in this meal as veg", on: "border-emerald-600 bg-emerald-600" },
  { value: "nonveg", label: "Non-veg", title: "Mark every dish in this meal as non-veg", on: "border-red-600 bg-red-600" },
  { value: "egg", label: "Egg", title: "Mark every dish in this meal as egg", on: "border-amber-500 bg-amber-500" },
];

export const FoodMenuTab = ({ stayId }) => {
  const [menu, setMenu] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [publicUrl, setPublicUrl] = useState("");
  const [newSpecial, setNewSpecial] = useState({ date: "", note: "" });
  const [showQr, setShowQr] = useState(false);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/stay/${stayId}/food-menu`);
      setMenu(data.menu);
      setSpecials(data.specials || []);
      setPublicUrl(data.public_url);
    } catch (err) {
      toast.error(errText(err));
    }
  }, [stayId]);

  useEffect(() => {
    load();
    api.get("/properties/mine").then((r) => setProperties(r.data || [])).catch(() => setProperties([]));
  }, [load]);

  if (!menu) return <p className="text-sm text-muted-foreground">Loading menu…</p>;

  const setItems = (day, slotKey, value) =>
    setMenu((m) => ({ ...m, days: { ...m.days, [day]: { ...(m.days?.[day] || {}), [slotKey]: value } } }));

  const setTag = (day, slotKey, value) =>
    setMenu((m) => ({ ...m, meal_tags: { ...(m.meal_tags || {}), [day]: { ...((m.meal_tags || {})[day] || {}), [slotKey]: value } } }));

  const toggleProperty = (pid) =>
    setMenu((m) => {
      const cur = m.property_ids || [];
      return { ...m, property_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] };
    });


  const setSlot = (i, patch) =>
    setMenu((m) => ({ ...m, slots: m.slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));

  const toggleSlot = (slot) =>
    setMenu((m) =>
      m.slots.some((s) => s.key === slot.key)
        ? { ...m, slots: m.slots.filter((s) => s.key !== slot.key) }
        : { ...m, slots: [...m.slots, { ...slot }] });

  const addCustomSlot = () => {
    const name = window.prompt("Name of the meal slot (e.g. Brunch)");
    if (!name?.trim()) return;
    const key = `c_${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    if (menu.slots.some((s) => s.key === key)) return toast.error("That slot already exists");
    setMenu((m) => ({ ...m, slots: [...m.slots, { key, name: name.trim(), time: "" }] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/stay/${stayId}/food-menu`, {
        slots: menu.slots,
        days: menu.days || {},
        meal_tags: menu.meal_tags || {},
        weekday_specials: menu.weekday_specials || {},
        property_ids: menu.property_ids || [],
        published: menu.published !== false,
        note: menu.note || "",
      });
      toast.success("Food menu saved");
      if (data?.dropped_property_ids?.length)
        toast.error("Some listings couldn't be linked — you can only link listings you own");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const addSpecial = async () => {
    if (!newSpecial.date || !newSpecial.note.trim()) return toast.error("Pick a date and what's special");
    try {
      await api.post(`/stay/${stayId}/food-specials`, newSpecial);
      setNewSpecial({ date: "", note: "" });
      toast.success("Special added");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const removeSpecial = async (date) => {
    try {
      await api.delete(`/stay/${stayId}/food-specials/${date}`);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const qrSrc = `${process.env.REACT_APP_BACKEND_URL}/api/public/menu/${menu.menu_token}/qr`;

  return (
    <div data-testid="stay-food-menu">
      <div className="rounded-2xl border border-border p-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Tenant menu link</p>
        <p className="mt-2 text-[13px] text-muted-foreground">
          Anyone with this link sees your menu — no login needed. Print the QR and stick it in the dining area.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <code className="rounded-lg bg-muted px-3 py-2 text-[12px] break-all" data-testid="food-menu-public-url">{publicUrl}</code>
          <Button
            variant="outline"
            className="h-10 rounded-lg"
            onClick={() => {
              navigator.clipboard?.writeText(publicUrl);
              toast.success("Link copied");
            }}
            data-testid="food-menu-copy-link"
          >
            <Copy className="h-4 w-4 mr-1.5" /> Copy
          </Button>
          <Button variant="outline" className="h-10 rounded-lg" onClick={() => setShowQr((v) => !v)} data-testid="food-menu-qr-btn">
            <QrCode className="h-4 w-4 mr-1.5" /> {showQr ? "Hide QR" : "Show QR"}
          </Button>
          <button
            onClick={() => setMenu({ ...menu, published: menu.published === false })}
            className={`rounded-full border px-4 py-2 text-[12px] font-bold transition-colors duration-200 ${
              menu.published === false ? "border-red-400 bg-red-500/5 text-red-600" : "border-emerald-500 bg-emerald-500/10 text-emerald-700"
            }`}
            data-testid="food-menu-publish-toggle"
          >
            {menu.published === false ? "Hidden from tenants" : "Visible to tenants"}
          </button>
        </div>
        {showQr && (
          <div className="mt-5">
            <img src={qrSrc} alt="Menu QR code" className="h-44 w-44 rounded-xl border border-border bg-white p-2" data-testid="food-menu-qr-image" />
            <a href={qrSrc} download="pg-food-menu-qr.png" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold hover:underline" data-testid="food-menu-qr-download">
              <Download className="h-3.5 w-3.5" /> Download QR
            </a>
          </div>
        )}
      </div>

      <div className="mt-7 rounded-2xl border border-border p-6">
        <p className="font-display text-lg">Meal slots &amp; timings</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {menu.slots.map((s, i) => (
            <div key={s.key} className="rounded-xl border border-border p-4" data-testid={`food-slot-${s.key}`}>
              <div className="flex items-center justify-between gap-2">
                <Input
                  value={s.name}
                  onChange={(e) => setSlot(i, { name: e.target.value })}
                  className="h-9 rounded-lg text-sm font-bold"
                  data-testid={`food-slot-name-${s.key}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-lg px-2 text-destructive"
                  onClick={() => setMenu({ ...menu, slots: menu.slots.filter((x) => x.key !== s.key) })}
                  data-testid={`food-slot-remove-${s.key}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Input
                value={s.time || ""}
                onChange={(e) => setSlot(i, { time: e.target.value })}
                placeholder="8:00 - 9:30 AM"
                className="mt-2 h-9 rounded-lg text-[12px]"
                data-testid={`food-slot-time-${s.key}`}
              />
            </div>
          ))}
        </div>

        <p className="mt-5 text-[12px] font-semibold text-muted-foreground">Optional slots</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {OPTIONAL_SLOTS.map((o) => {
            const on = menu.slots.some((s) => s.key === o.key);
            return (
              <label
                key={o.key}
                className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors duration-200 ${
                  on ? "border-foreground bg-muted" : "border-border hover:border-foreground/50"
                }`}
                data-testid={`food-optional-slot-${o.key}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggleSlot(o)} className="h-3.5 w-3.5 accent-black" />
                {o.name}
              </label>
            );
          })}
          <button
            onClick={addCustomSlot}
            className="flex items-center gap-1.5 rounded-full border border-dashed border-input px-4 py-2 text-[12px] font-semibold transition-colors duration-200 hover:border-foreground"
            data-testid="food-add-custom-slot"
          >
            <Plus className="h-3.5 w-3.5" /> Custom slot
          </button>
        </div>
      </div>

      <div className="mt-7 space-y-4">
        {DAYS.map(([key, label]) => (
          <div key={key} className="rounded-2xl border border-border p-6" data-testid={`food-day-${key}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-display text-lg">{label}</p>
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <Input
                  value={menu.weekday_specials?.[key] || ""}
                  onChange={(e) =>
                    setMenu({ ...menu, weekday_specials: { ...(menu.weekday_specials || {}), [key]: e.target.value } })
                  }
                  placeholder={`Every ${label} special`}
                  className="h-9 w-60 rounded-lg text-[12px]"
                  data-testid={`food-weekday-special-${key}`}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {menu.slots.map((s) => (
                <div key={s.key} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs font-semibold">{s.name}</Label>
                    <div className="flex items-center gap-0.5">
                      {TAG_OPTIONS.map((t) => {
                        const on = (menu.meal_tags?.[key]?.[s.key] || "") === t.value;
                        return (
                          <button
                            key={t.value || "auto"}
                            onClick={() => setTag(key, s.key, t.value)}
                            title={t.title}
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors duration-200 ${
                              on ? `${t.on} text-white` : "border-border text-muted-foreground hover:border-foreground/50"
                            }`}
                            data-testid={`food-tag-${key}-${s.key}-${t.value || "auto"}`}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <Textarea
                    value={menu.days?.[key]?.[s.key] || ""}
                    onChange={(e) => setItems(key, s.key, e.target.value)}
                    placeholder="Idli, Sambar, Chutney"
                    className="min-h-[74px] rounded-lg text-[13px]"
                    data-testid={`food-items-${key}-${s.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-7 rounded-2xl border border-border p-6">
        <p className="font-display text-lg">Show this menu on your listings</p>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Pick the property listings where house hunters should see today's food — it appears on the listing page.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {properties.length === 0 && <p className="text-[13px] text-muted-foreground" data-testid="food-no-properties">You have no listings yet.</p>}
          {properties.map((p) => {
            const on = (menu.property_ids || []).includes(p.property_id);
            return (
              <label
                key={p.property_id}
                className={`flex max-w-full cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-semibold transition-colors duration-200 ${
                  on ? "border-foreground bg-muted" : "border-border hover:border-foreground/50"
                }`}
                data-testid={`food-listing-${p.property_id}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggleProperty(p.property_id)} className="h-3.5 w-3.5 accent-black" />
                <span className="truncate">{p.title}</span>
                <span className="text-muted-foreground">{p.city}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-7 rounded-2xl border border-border p-6">
        <p className="font-display text-lg">Note for tenants</p>
        <Textarea
          value={menu.note || ""}
          onChange={(e) => setMenu({ ...menu, note: e.target.value })}
          placeholder="Menu may change based on availability. Inform the cook by 9 PM for special requests."
          className="mt-3 min-h-[74px] rounded-lg text-[13px]"
          data-testid="food-menu-note"
        />
      </div>

      <div className="sticky bottom-4 mt-7 flex justify-end">
        <Button className="h-12 rounded-lg font-bold shadow-lg hover:brightness-95" onClick={save} disabled={saving} data-testid="food-menu-save-btn">
          {saving ? "Saving…" : "Save menu"}
        </Button>
      </div>

      <div className="mt-10 rounded-2xl border border-border p-6">
        <p className="font-display text-lg">Special days (by date)</p>
        <p className="mt-1 text-[13px] text-muted-foreground">Festival feasts, birthdays, anything one-off.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Date</Label>
            <Input
              type="date"
              value={newSpecial.date}
              onChange={(e) => setNewSpecial({ ...newSpecial, date: e.target.value })}
              className="h-11 w-44 rounded-lg"
              data-testid="food-special-date-input"
            />
          </div>
          <div className="space-y-1.5 flex-1 min-w-[220px]">
            <Label className="text-xs font-semibold">What's special</Label>
            <Input
              value={newSpecial.note}
              onChange={(e) => setNewSpecial({ ...newSpecial, note: e.target.value })}
              placeholder="Chicken Biryani + Gulab Jamun"
              className="h-11 rounded-lg"
              data-testid="food-special-note-input"
            />
          </div>
          <Button className="h-11 rounded-lg font-bold hover:brightness-95" onClick={addSpecial} data-testid="food-special-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add
          </Button>
        </div>

        <div className="mt-5 space-y-2">
          {specials.length === 0 && <p className="text-[13px] text-muted-foreground" data-testid="food-no-specials">No special days added yet.</p>}
          {specials.map((s) => (
            <div key={s.date} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3" data-testid={`food-special-${s.date}`}>
              <p className="text-[13px]">
                <span className="font-bold">{s.date}</span> <span className="text-muted-foreground">{s.note}</span>
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-lg px-2 text-destructive"
                onClick={() => removeSpecial(s.date)}
                data-testid={`food-special-delete-${s.date}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
